// app/components/privacy-graph-scrolly/keyframe-data.ts
//
// Data model for the 6-beat privacy-graph scrollytelling.
// Coordinates are derived from the ELK-precomputed layout in graph-layout.json.
// Positions in this file MUST NOT use Math.random() or Date.now() — SSR-CSR
// determinism is enforced by the snapshot test in __tests__/snapshot.test.tsx.

import type { FloatingIcon, KeyframeBand, KeyframeIndex } from "./types";
import {
  LAYOUT,
  FEATURED_WORKLOAD_NODES,
  PERSONAL_DATA_NODES,
} from "./graph-layout";

// ---------------------------------------------------------------------------
// FLOATING_ICONS — focused workload nodes scattered to deterministic positions
// during beats 1–2. Hand-picked so each card travels a meaningful distance to
// its settled position AND no two cards' floating bounds overlap (avoids the
// S3-behind-Snapshot artifact that happens with hash-based perturbation).
// SSR-deterministic — never uses Math.random or Date.now.
// ---------------------------------------------------------------------------

const PII = new Set<string>(PERSONAL_DATA_NODES);

const FLOAT_POS: Record<string, { x: number; y: number }> = {
  // Stagger across the canvas. Each (x, y) is a card center; cards are
  // 150×62, so allow ~80px clearance around each point.
  "api-gw":   { x: 220, y: 290 },
  "lambda":   { x: 480, y: 250 },
  "bedrock":  { x: 730, y: 300 },
  "s3":       { x: 360, y: 180 },
  "dynamo":   { x: 590, y: 130 },
  "ddb-snap": { x: 850, y: 200 },
};

export const FLOATING_ICONS: FloatingIcon[] = LAYOUT.nodes.map((n) => {
  const pos = FLOAT_POS[n.id] ?? { x: n.cx, y: n.cy };
  return {
    id: n.id,
    shortCode: shortCodeFor(n.id),
    x: pos.x,
    y: pos.y,
    driftSeed: driftSeedFor(n.id),
    isProduct: true,
    isPii: PII.has(n.id),
  };
});

function shortCodeFor(id: string): string {
  const map: Record<string, string> = {
    "api-gw":   "API",
    "lambda":   "λ",
    "bedrock":  "BDK",
    "dynamo":   "DDB",
    "rds":      "RDS",
    "s3":       "S3",
    "cw-logs":  "CWL",
    "ddb-snap": "DSNP",
    "rds-snap": "RSNP",
  };
  return map[id] ?? id.toUpperCase().slice(0, 4);
}

function driftSeedFor(id: string): number {
  const map: Record<string, number> = {
    "api-gw":   0.0,
    "lambda":   0.6,
    "bedrock":  1.2,
    "dynamo":   1.8,
    "rds":      2.4,
    "s3":       3.0,
    "cw-logs":  3.6,
    "ddb-snap": 4.2,
    "rds-snap": 4.8,
  };
  return map[id] ?? 0.0;
}

// ---------------------------------------------------------------------------
// Featured workload (Beat 4)
// ---------------------------------------------------------------------------

export const FEATURED_WORKLOAD = {
  // Empty label — modal carries the workload name to avoid label/modal overlap.
  label: "",
  nodes: FEATURED_WORKLOAD_NODES,
  padding: 24,
  labelOffsetY: -14,
} as const;

export const PERIPHERAL_HINT = "+ peripheral workloads";

// ---------------------------------------------------------------------------
// Peripheral nodes (Beats 1–2)
// Smaller, lower-opacity cards that show the scan covers more than just the
// focused workload. Hand-placed positions stay clear of where the focused 6
// floating icons can drift (±90px from their tier-snap positions).
// They fade out during the beat 3 settle.
// ---------------------------------------------------------------------------

export interface PeripheralNode {
  id: string;
  label: string;
  /** Top-left x in SVG coordinates. */
  x: number;
  /** Top-left y in SVG coordinates. */
  y: number;
}

export const PERIPHERAL_NODES: readonly PeripheralNode[] = [
  // Top row (y < focused-card float range)
  { id: "rds",         label: "RDS",         x:  60, y:  44 },
  { id: "ecs",         label: "ECS",         x: 220, y:  60 },
  { id: "sqs",         label: "SQS",         x: 420, y:  44 },
  { id: "cognito",     label: "Cognito",     x: 600, y:  60 },
  { id: "cw-logs",     label: "CloudWatch",  x: 800, y:  44 },
  // Mid-edge (well clear of focused-card float bounds)
  { id: "kinesis",     label: "Kinesis",     x:  15, y: 200 },
  { id: "step-fns",    label: "Step Fns",    x: 905, y: 100 },
  { id: "glue",        label: "Glue",        x: 905, y: 270 },
  // Bottom row (y > focused-card float range)
  { id: "eventbridge", label: "EventBridge", x:  60, y: 388 },
  { id: "secrets",     label: "Secrets Mgr", x: 300, y: 400 },
  { id: "sns",         label: "SNS",         x: 560, y: 380 },
  { id: "ses",         label: "SES",         x: 880, y: 388 },
];

// ---------------------------------------------------------------------------
// Drift node (Beat 5) — id is now "bedrock"; AI/ML lane materializes from beat 5.
// Position comes directly from ELK layout (bedrock has tier=2, AI/ML lane).
// Visibility is gated per-beat at render time.
// ---------------------------------------------------------------------------

export const DRIFT_NODE_ID = "bedrock" as const;

// ---------------------------------------------------------------------------
// Modal data — beats 4, 5, 6 each render exactly one modal in the upper-left
// callout slot. Visual structure shared via <Modal> element.
// ---------------------------------------------------------------------------

export interface ModalRow {
  label: string;
  value: string;
  /** When true, row is rendered as a nested child (indented + connector hairline). */
  nested?: boolean;
  /** When true, the value renders with a sage check glyph before the text. */
  ok?: boolean;
}

export interface SavingsRow {
  label: string;
  value: string;
}

export interface BeatModalData {
  status: "ok" | "warning" | "drift";
  eyebrow: string;
  title: string;
  /** Body lines or rows — strings render as paragraphs; ModalRow as label/value rows. */
  body: readonly (string | ModalRow)[];
  savings?: SavingsRow;
}

export const WORKLOAD_MODAL_DATA: BeatModalData = {
  status:  "ok",
  eyebrow: "STABLE",
  title:   "customer-order workload",
  body: [
    { label: "Last review", value: "Completed", ok: true },
    { label: "Privacy Review",  value: "Approved",  ok: true, nested: true },
    { label: "RoPA",            value: "Linked",    ok: true, nested: true },
    { label: "DPIA",            value: "Approved",  ok: true, nested: true },
  ],
};

export const DRIFT_MODAL_DATA: BeatModalData = {
  status:  "drift",
  eyebrow: "DRIFT",
  title:   "Privacy impacting drift detected",
  body: [
    "Bedrock integration added to application that processes Personal Data.",
  ],
};

export const FINDING_MODAL_DATA: BeatModalData = {
  status:  "warning",
  eyebrow: "FINDING",
  title:   "No retention period on DynamoDB",
  body: [
    "Personal Data retained indefinitely.",
    "Risk propagates to manual snapshots (14×).",
  ],
  savings: { label: "EST. SAVINGS", value: "$10k / yr" },
};

// ---------------------------------------------------------------------------
// Snap-fade window — fraction of total scroll used for inter-beat transitions.
// Wider than the previous 0.015 so each state-change reads as a discrete moment
// even when the user scrolls quickly. Hold time per beat stays >70% of the band.
// ---------------------------------------------------------------------------
// Width of the cross-fade window between beats, expressed as a fraction of
// total scroll progress. Capped at 0.04 because beats 4 and 5 contain phased
// modal animations whose input ranges include start + FADE * 2 and
// nextBand.start - FADE — for those to stay monotonic, FADE * 3 must fit
// inside the narrowest band. Narrowest band post-retune is B1 at 0.08, so
// 0.04 * 3 = 0.12 leaves sufficient hold inside the band. Future retunes that
// shrink any band below 0.12 must also revisit this constant.
export const SNAP_FADE = 0.04;

// ---------------------------------------------------------------------------
// Keyframe bands — single sticky section, 6 contiguous beats. Beat 3 is
// extended to 0.50 so the assembled-graph state holds for the second half
// of its band before beat 4 begins, giving the user a moment to read the
// composed graph without a hard cut into the workload zone.
// ---------------------------------------------------------------------------

export const KEYFRAME_BANDS: readonly KeyframeBand[] = [
  { index: 1, start: 0.00, end: 0.08 },  // unmapped — quick read
  { index: 2, start: 0.08, end: 0.22 },  // scan sweep — needs travel time
  { index: 3, start: 0.22, end: 0.50 },  // graph emerges + assembled-graph hold
  { index: 4, start: 0.50, end: 0.60 },  // workload zone forms
  { index: 5, start: 0.60, end: 0.76 },  // drift — surprise moment
  { index: 6, start: 0.76, end: 1.00 },  // findings cascade + held end-state
];

// Mobile keeps the same six-beat story, but gives the workload/review state
// more dwell so it is not skipped by a normal finger scroll.
export const MOBILE_KEYFRAME_BANDS: readonly KeyframeBand[] = [
  { index: 1, start: 0.00, end: 0.10 },
  { index: 2, start: 0.10, end: 0.25 },
  { index: 3, start: 0.25, end: 0.45 },
  { index: 4, start: 0.45, end: 0.63 },
  { index: 5, start: 0.63, end: 0.78 },
  { index: 6, start: 0.78, end: 1.00 },
] as const;

// ---------------------------------------------------------------------------
// Per-pin keyframe bands — Pin 1 holds beats 1-3, Pin 2 holds beats 4-6.
// Each pin's progress runs 0→1 independently. Widths here are passthroughs
// from the original 6-band proportions (preserved during structural refactor);
// the asymmetric retune happens later (see Task 11).
// ---------------------------------------------------------------------------

export const PIN1_BANDS: readonly KeyframeBand[] = [
  { index: 1, start: 0.0,  end: 0.32 },
  { index: 2, start: 0.32, end: 0.64 },
  { index: 3, start: 0.64, end: 1.0  },
];

export const PIN2_BANDS: readonly KeyframeBand[] = [
  { index: 4, start: 0.0,  end: 0.25 },
  { index: 5, start: 0.25, end: 0.52 },
  { index: 6, start: 0.52, end: 1.0  },
];

// ---------------------------------------------------------------------------
// Per-beat headers — eyebrow / title / description
// ---------------------------------------------------------------------------

export interface BeatHeader {
  eyebrow: string;
  title: string;
  description: string;
}

export const BEAT_HEADERS: Record<KeyframeIndex, BeatHeader> = {
  1: {
    eyebrow: "01 / DISCOVER",
    title: "Your unmapped cloud.",
    description: "Resources scattered across accounts with no visibility",
  },
  2: {
    eyebrow: "02 / SCAN",
    title: "A read-only scan begins.",
    description: "Truspecta inventories privacy-relevant assets using only metadata",
  },
  3: {
    eyebrow: "03 / GRAPH",
    title: "Resources, data stores, and data flows discovered.",
    description: "A privacy graph emerges from your real infrastructure",
  },
  4: {
    eyebrow: "04 / WORKLOADS",
    title: "Resources are categorized into workloads.",
    description: "Privacy reviews and artifacts attach to the workload they belong to",
  },
  5: {
    eyebrow: "05 / DRIFT",
    title: "Privacy-impacting drift surfaces.",
    description: "New resources trigger reviews before risk compounds",
  },
  6: {
    eyebrow: "06 / FINDINGS",
    title: "Findings trace back to workloads.",
    description: "Every finding links to the resource, the workload, and the remediation",
  },
} as const;

// ---------------------------------------------------------------------------
// Compat shims — referenced by stacked-static.tsx (deleted at B16).
// Do not use in new code.
// ---------------------------------------------------------------------------
/** @deprecated */
export const DRIFT_NODE = {
  x: 820, y: 160, w: 134, h: 84,
  label: "Bedrock",
} as const;

/** @deprecated */
export const DRIFT_BADGE = {
  label: "DRIFT",
  yOffsetFromEdge: 14,
} as const;

/** @deprecated */
export const CHECKPOINT_CARD = {
  w: 200, h: 60,
  title: "Privacy Review",
  body: "Approved",
} as const;

/** @deprecated */
export const OWNER_PILL = {
  w: 100, h: 22,
  label: "privacy-team",
  insetFromZoneTopRight: { x: 12, y: 12 },
  h_: 22,
} as const;

/** @deprecated */
export const ROPA_CARD = {
  w: 180, h: 52,
  title: "RoPA Entry",
  subtitle: "Linked",
} as const;

/** @deprecated */
export const SAVINGS_ANNOTATION = {
  label: "Est. $10k/yr exposure",
} as const;
